import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { getMicrosoftRedirectUri, silentAuthRequest } from '../auth/authConfig';
import { Routes } from '../routes';
import { login, useAppDispatch } from '../stores';
import {
    clearCollectInviteReturnStorage,
    peekCollectInviteReturn,
} from '../utils/collectInviteRedirect';
import {
    navigateToAutomationRedirect,
    peekAutomationRedirect,
} from '../utils/automationRedirect';
import { markAuthSyncGracePeriod, persistAuthTokens } from '../utils/authUtils';
import { handleLoginSuccess } from '../utils/loginResponseHandler';
import {
    clearMarketplaceLoginPending,
    getMarketplaceToken,
    shouldResumeMarketplaceFlow,
} from '../utils/marketplaceSession';

type UseMicrosoftFamlaSessionOptions = {
    enabled?: boolean;
    redirectQueryParam?: string | null;
    onStart?: () => void;
    onError?: (error: unknown) => void;
};

type UseMicrosoftFamlaSessionResult = {
    isLoading: boolean;
};

export function useMicrosoftFamlaSession({
    enabled = true,
    redirectQueryParam = null,
    onStart,
    onError,
}: UseMicrosoftFamlaSessionOptions = {}): UseMicrosoftFamlaSessionResult {
    const { accounts, instance } = useMsal();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = React.useState(false);
    const activeRequestRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (!enabled) {
            setIsLoading(false);
            activeRequestRef.current = null;
            return;
        }

        if (accounts.length === 0) {
            setIsLoading(false);
            activeRequestRef.current = null;
            return;
        }

        const manualLogout = sessionStorage.getItem('microsoft_manual_logout');
        if (manualLogout === 'true') {
            console.log('⏸️ [Microsoft SSO] Manual logout detected, skipping auto-login');
            setIsLoading(false);
            return;
        }

        const account = accounts[0];
        const requestKey = account.homeAccountId || account.username;
        if (activeRequestRef.current === requestKey) return;
        activeRequestRef.current = requestKey;

        const createFamlaSession = async () => {
            try {
                console.log('✅ [Microsoft SSO] Microsoft account detected, creating Famla session...', account);
                setIsLoading(true);
                onStart?.();

                let tokenResponse;
                try {
                    tokenResponse = await instance.acquireTokenSilent({
                        ...silentAuthRequest,
                        account,
                    });
                } catch (tokenError) {
                    if (tokenError instanceof InteractionRequiredAuthError) {
                        await instance.acquireTokenRedirect({
                            ...silentAuthRequest,
                            account,
                            redirectUri: getMicrosoftRedirectUri('/auth/login'),
                        });
                        return;
                    }
                    throw tokenError;
                }

                const apiUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:9540/api/v1';
                const response = await fetch(`${apiUrl}/auth/microsoft-bearer`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${tokenResponse.accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(await response.text());
                }

                const data = await response.json();
                const sessionData = data.data;

                dispatch(login({
                    token: sessionData.access_token,
                    session: sessionData.session,
                    user: sessionData.session.user,
                }));

                persistAuthTokens(sessionData.access_token, sessionData.refresh_token);
                markAuthSyncGracePeriod();
                handleLoginSuccess(data);
                sessionStorage.removeItem('microsoft_auth_intent');
                clearMarketplaceLoginPending();

                if (shouldResumeMarketplaceFlow()) {
                    const marketplaceToken = getMarketplaceToken();
                    if (!marketplaceToken) {
                        // fall through to normal navigation
                    } else {
                        const onMarketplacePage = window.location.pathname.startsWith('/microsoft-purchase');
                        if (!onMarketplacePage) {
                            navigate(
                                `/microsoft-purchase?token=${encodeURIComponent(marketplaceToken)}`,
                                { replace: true },
                            );
                        }
                        return;
                    }
                }

                const collectReturn = peekCollectInviteReturn(redirectQueryParam);
                if (sessionData.session.company_id !== null && collectReturn) {
                    clearCollectInviteReturnStorage();
                    navigate(collectReturn, { replace: true });
                    return;
                }

                const automationReturn = peekAutomationRedirect(redirectQueryParam);
                if (sessionData.session.company_id !== null && automationReturn) {
                    navigateToAutomationRedirect(automationReturn);
                    return;
                }

                const invitedProcessId = sessionStorage.getItem('invited_process_id');
                if (sessionData.session.company_id === null) {
                    navigate(Routes.CREATE_MY_COMPANY);
                } else if (invitedProcessId) {
                    navigate(`${Routes.PROCESS_INTERVIEW}${invitedProcessId}`, { replace: true });
                } else {
                    navigate(Routes.HOMEVIEW);
                }
            } catch (error) {
                console.error('[Microsoft SSO] Error creating Famla session:', error);
                clearMarketplaceLoginPending();
                activeRequestRef.current = null;
                setIsLoading(false);
                onError?.(error);
            }
        };

        createFamlaSession();
    }, [accounts, dispatch, enabled, instance, navigate, onError, onStart, redirectQueryParam]);

    return { isLoading };
}

export default useMicrosoftFamlaSession;
